/* =========================================================
   Verificación en dos pasos, desde la propia cuenta.

   El servidor sabía comprobar un código TOTP al entrar desde el primer
   día, pero no había ninguna pantalla para activarlo: sabía verificar
   un código que nadie podía llegar a tener.

   Son tres pasos y ninguno se salta:

     1. La contraseña, para que una sesión robada no pueda atar un
        segundo factor que el dueño de la cuenta no tiene.
     2. El QR (y el secreto en texto, por si el teléfono no puede
        escanear).
     3. Un código de la aplicación, que demuestra que el QR se escaneó
        BIEN. Sin este paso, un QR mal leído dejaría a la persona fuera
        de su propia cuenta en el siguiente inicio de sesión.

   No hay códigos de recuperación porque no hay correo con el que
   entregarlos. Quien pierda el teléfono llama a ETC y el Admin se lo
   retira desde Usuarios y roles.

   El QR se dibuja aquí, sin librería ni servicio externo: mandar el
   otpauth:// —que lleva el secreto dentro— a la API de códigos QR de
   un tercero sería regalar el segundo factor.
   ========================================================= */
import { useEffect, useState } from 'react';
import { ic, sized } from '@/components/Icons';
import { Modal } from './Modal';
import { api } from '@/lib/api';
import { useI18n } from '@/i18n';
import { qrMatrix } from '@/lib/qr';
import { PasswordInput } from './PasswordInput';

type Paso = 'estado' | 'password' | 'qr' | 'retirar';

interface Estado { enabled: boolean; pending: boolean }

export function SecurityModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { lang } = useI18n();
  const [paso, setPaso] = useState<Paso>('estado');
  const [estado, setEstado] = useState<Estado | null>(null);
  const [password, setPassword] = useState('');
  const [codigo, setCodigo] = useState('');
  const [secreto, setSecreto] = useState<{ secret: string; uri: string } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [fallo, setFallo] = useState('');
  const [hecho, setHecho] = useState('');

  const es = lang === 'es';

  function reiniciar() {
    setPaso('estado');
    setPassword('');
    setCodigo('');
    setSecreto(null);
    setFallo('');
    setHecho('');
    setOcupado(false);
  }

  useEffect(() => {
    if (!open) return;
    reiniciar();
    void api.get<Estado>('/api/auth/totp')
      .then(setEstado)
      .catch((err: unknown) => setFallo(err instanceof Error ? err.message : String(err)));
  }, [open]);

  async function pedirSecreto() {
    setOcupado(true);
    setFallo('');
    try {
      setSecreto(await api.post<{ secret: string; uri: string }>('/api/auth/totp/enroll', { password }));
      setPaso('qr');
      setCodigo('');
    } catch (err) {
      setFallo(err instanceof Error ? err.message : String(err));
    } finally {
      setOcupado(false);
    }
  }

  async function confirmar() {
    setOcupado(true);
    setFallo('');
    try {
      await api.post('/api/auth/totp/confirm', { code: codigo });
      setEstado({ enabled: true, pending: false });
      setHecho(es
        ? 'Listo. A partir de ahora, al entrar te pedirá el código.'
        : 'Done. From now on you will be asked for the code when signing in.');
      setPaso('estado');
      setPassword('');
      setSecreto(null);
    } catch (err) {
      setFallo(err instanceof Error ? err.message : String(err));
    } finally {
      setOcupado(false);
    }
  }

  async function retirar() {
    setOcupado(true);
    setFallo('');
    try {
      await api.post('/api/auth/totp/disable', { password, code: codigo || undefined });
      setEstado({ enabled: false, pending: false });
      setHecho(es
        ? 'Se retiró la verificación en dos pasos. Tu cuenta queda protegida solo por la contraseña.'
        : 'Two-step verification removed. Your account is now protected by the password alone.');
      setPaso('estado');
      setPassword('');
      setCodigo('');
    } catch (err) {
      setFallo(err instanceof Error ? err.message : String(err));
    } finally {
      setOcupado(false);
    }
  }

  const aviso = fallo ? (
    <div
      role="alert"
      style={{
        display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12, padding: '10px 12px',
        border: '1px solid var(--err)', borderRadius: 8, fontSize: 12.5, color: 'var(--err)',
      }}
    >
      <span style={{ flexShrink: 0, display: 'inline-flex' }}>{ic.warn}</span>
      <span>{fallo}</span>
    </div>
  ) : null;

  const caja = {
    padding: '9px 11px', borderRadius: 6, fontSize: 13, width: '100%',
    border: '1px solid var(--line-2)', background: 'var(--bg-1)', color: 'inherit',
  } as const;

  const titulo = es ? 'Verificación en dos pasos' : 'Two-step verification';

  return (
    <Modal
      open={open}
      onClose={ocupado ? () => {} : onClose}
      title={titulo}
      width={520}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={ocupado}>
            {es ? 'Cerrar' : 'Close'}
          </button>
          {paso === 'password' ? (
            <button
              className="btn primary"
              onClick={() => void pedirSecreto()}
              disabled={ocupado || password.length === 0}
            >
              {ocupado ? (es ? 'Comprobando…' : 'Checking…') : (es ? 'Continuar' : 'Continue')}
            </button>
          ) : paso === 'qr' ? (
            <button
              className="btn primary"
              onClick={() => void confirmar()}
              disabled={ocupado || !/^\d{6}$/.test(codigo)}
            >
              {ocupado ? (es ? 'Activando…' : 'Enabling…') : (es ? 'Activar' : 'Enable')}
            </button>
          ) : paso === 'retirar' ? (
            <button
              className="btn danger"
              onClick={() => void retirar()}
              disabled={ocupado || password.length === 0}
            >
              {ocupado ? (es ? 'Retirando…' : 'Removing…') : (es ? 'Retirar' : 'Remove')}
            </button>
          ) : null}
        </>
      }
    >
      {aviso}

      {hecho ? (
        <div
          style={{
            display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12, padding: '10px 12px',
            border: '1px solid var(--ok)', borderRadius: 8, fontSize: 12.5, color: 'var(--ok)',
          }}
        >
          <span style={{ flexShrink: 0, display: 'inline-flex' }}>{sized(ic.check, 16)}</span>
          <span>{hecho}</span>
        </div>
      ) : null}

      {/* ---------- estado ---------- */}
      {paso === 'estado' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-2)' }}>
            {es
              ? 'Un código de seis dígitos, que cambia cada treinta segundos, además de tu contraseña. Si alguien se hace con tu contraseña, sin el teléfono no entra.'
              : 'A six-digit code that changes every thirty seconds, on top of your password. If someone gets your password, they still cannot get in without your phone.'}
          </p>

          {estado == null ? (
            <div style={{ fontSize: 13, color: 'var(--text-3)' }}>{es ? 'Cargando…' : 'Loading…'}</div>
          ) : estado.enabled ? (
            <>
              <div
                style={{
                  display: 'flex', gap: 8, alignItems: 'center', padding: '10px 12px',
                  border: '1px solid var(--ok)', borderRadius: 8, fontSize: 13,
                }}
              >
                <span style={{ display: 'inline-flex', color: 'var(--ok)' }}>{sized(ic.check, 16)}</span>
                <strong>{es ? 'Activa en esta cuenta.' : 'Enabled on this account.'}</strong>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                {es
                  ? 'Para cambiar de teléfono: retírala aquí y vuelve a activarla con el aparato nuevo. Si perdiste el teléfono y no puedes generar códigos, pídele al equipo de ETC que la retire desde Usuarios y roles.'
                  : 'To switch phones: remove it here and enable it again on the new device. If you lost the phone and cannot generate codes, ask the ETC team to remove it from Users and roles.'}
              </div>
              <button className="btn danger" onClick={() => { setPaso('retirar'); setFallo(''); setHecho(''); }}>
                {es ? 'Retirar la verificación' : 'Remove verification'}
              </button>
            </>
          ) : (
            <>
              <div
                style={{
                  display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px',
                  border: '1px solid var(--warn)', borderRadius: 8, fontSize: 13,
                }}
              >
                <span style={{ flexShrink: 0, display: 'inline-flex', color: 'var(--warn)' }}>{ic.warn}</span>
                <span>
                  {estado.pending
                    ? (es
                        ? 'Empezaste a configurarla y quedó a medias. Vuelve a empezar: el código anterior ya no sirve.'
                        : 'You started setting it up and left it half done. Start again: the earlier code no longer works.')
                    : (es
                        ? 'No está activa. Tu cuenta está protegida solo por la contraseña.'
                        : 'Not enabled. Your account is protected by the password alone.')}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
                {es
                  ? 'Necesitas una aplicación de códigos en el teléfono: Microsoft Authenticator, Google Authenticator o similar.'
                  : 'You need a code app on your phone: Microsoft Authenticator, Google Authenticator or similar.'}
              </div>
              <button className="btn primary" onClick={() => { setPaso('password'); setFallo(''); setHecho(''); }}>
                {es ? 'Activar la verificación' : 'Enable verification'}
              </button>
            </>
          )}
        </div>
      ) : null}

      {/* ---------- 1. contraseña ---------- */}
      {paso === 'password' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-2)' }}>
            {es
              ? 'Escribe tu contraseña. Es para asegurarnos de que eres tú y no alguien que se sentó en tu sitio con la sesión abierta.'
              : 'Type your password. This confirms it is you and not someone who sat down at your open session.'}
          </p>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>
              {es ? 'Tu contraseña' : 'Your password'}
            </span>
            <PasswordInput
              autoFocus
              style={caja}
              value={password}
              disabled={ocupado}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && password) void pedirSecreto(); }}
            />
          </label>
        </div>
      ) : null}

      {/* ---------- 2 y 3. QR y confirmación ---------- */}
      {paso === 'qr' && secreto ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-2)' }}>
            {es
              ? 'Abre tu aplicación de códigos, añade una cuenta nueva y escanea esto:'
              : 'Open your code app, add a new account, and scan this:'}
          </p>

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {(() => {
              const qr = qrMatrix(secreto.uri);
              return (
                // fondo blanco y módulos negros fijos, no tokens del tema: un
                // QR en gris sobre gris oscuro no lo lee ningún teléfono
                <svg
                  viewBox={`0 0 ${qr.size} ${qr.size}`}
                  role="img"
                  aria-label={es ? 'Código QR de configuración' : 'Setup QR code'}
                  style={{
                    width: 216, height: 216, background: '#fff',
                    borderRadius: 8, border: '1px solid var(--line-2)',
                  }}
                >
                  <path d={qr.path} fill="#000" shapeRendering="crispEdges" />
                </svg>
              );
            })()}
          </div>

          <details style={{ fontSize: 12 }}>
            <summary style={{ cursor: 'pointer', color: 'var(--text-2)' }}>
              {es ? 'No puedo escanear el código' : 'I cannot scan the code'}
            </summary>
            <div style={{ marginTop: 8, color: 'var(--text-2)' }}>
              {es
                ? 'Elige «introducir clave manualmente» en tu aplicación y teclea esto:'
                : 'Choose “enter key manually” in your app and type this:'}
              <div
                className="mono"
                style={{
                  marginTop: 6, padding: '8px 10px', borderRadius: 6, wordBreak: 'break-all',
                  border: '1px solid var(--line-2)', background: 'var(--bg-1)', fontSize: 12.5,
                }}
              >
                {secreto.secret}
              </div>
            </div>
          </details>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>
              {es ? 'Escribe el código que muestra la aplicación' : 'Type the code your app shows'}
            </span>
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              className="mono"
              style={{ ...caja, fontSize: 20, letterSpacing: '.28em', textAlign: 'center' }}
              value={codigo}
              disabled={ocupado}
              onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => { if (e.key === 'Enter' && /^\d{6}$/.test(codigo)) void confirmar(); }}
            />
          </label>
          <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
            {es
              ? 'Todavía no está activa: hasta que ese código no coincida, sigues entrando solo con la contraseña. Es a propósito, para que un QR mal leído no te deje fuera.'
              : 'It is not enabled yet: until that code matches, you keep signing in with the password alone. That is deliberate, so a badly scanned QR cannot lock you out.'}
          </div>
        </div>
      ) : null}

      {/* ---------- retirar ---------- */}
      {paso === 'retirar' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-2)' }}>
            {es
              ? 'Hacen falta tu contraseña y un código en vigor. Si tuvieras solo la contraseña, quien te robara la sesión podría quitar justo lo que protege la cuenta.'
              : 'Both your password and a current code are required. With the password alone, whoever stole your session could remove the very thing protecting the account.'}
          </p>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>
              {es ? 'Tu contraseña' : 'Your password'}
            </span>
            <PasswordInput
              autoFocus
              style={caja}
              value={password}
              disabled={ocupado}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>
              {es ? 'Código de la aplicación' : 'Code from your app'}
            </span>
            <input
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              className="mono"
              style={{ ...caja, letterSpacing: '.22em', textAlign: 'center' }}
              value={codigo}
              disabled={ocupado}
              onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
          </label>
        </div>
      ) : null}
    </Modal>
  );
}
